import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DsmDealHeader } from '../models/dsm-aps.models';

export interface RecentContract {
  dealId: string;
  prospectName: string;
  lotNo: string;
  modelName: string;
  elevationName: string;
  projectName: string;
  salesOfficeName: string;
  salesOfficeId: string;
  contractDate: string | null;
  viewedAt: number;
}

@Injectable({ providedIn: 'root' })
export class DsmRecentContractsService {
  readonly #STORAGE_KEY = 'dsm-recent-contracts';
  readonly #MAX = 8;
  #platformId = inject(PLATFORM_ID);

  /** Track a viewed contract from the APS detail page header data. */
  trackFromHeader(deal: DsmDealHeader, salesOfficeId: string, salesOfficeName: string): void {
    this.track({
      dealId: deal.id,
      prospectName: deal.prospect?.name ?? '',
      lotNo: deal.lot?.lotNo ?? '',
      modelName: deal.model?.modelName ?? '',
      elevationName: deal.elevation?.elevationCode ?? '',
      projectName: deal.project?.projectName ?? deal.project?.projectCode ?? '',
      salesOfficeName,
      salesOfficeId,
      contractDate: null
    });
  }
  track(entry: Omit<RecentContract, 'viewedAt'>): void {
    if (!isPlatformBrowser(this.#platformId)) return;
    const userId = this.#userId();
    if (!userId) return;

    const all = this.getAll();
    const previous = all.find(c => c.dealId === entry.dealId);
    const merged: RecentContract = {
      ...entry,
      salesOfficeId: entry.salesOfficeId || previous?.salesOfficeId || '',
      salesOfficeName: entry.salesOfficeName || previous?.salesOfficeName || '',
      contractDate: entry.contractDate ?? previous?.contractDate ?? null,
      viewedAt: Date.now()
    };

    const list = all.filter(c => c.dealId !== entry.dealId);
    list.unshift(merged);
    localStorage.setItem(`${userId}-${this.#STORAGE_KEY}`, JSON.stringify(list.slice(0, this.#MAX)));
  }

  /** Update the contractDate of an already-tracked entry (called when buyer contracts data loads). */
  updateContractDate(dealId: string, contractDate: string | null | undefined): void {
    if (!isPlatformBrowser(this.#platformId) || !contractDate) return;
    const userId = this.#userId();
    if (!userId) return;
    const list = this.getAll();
    const entry = list.find(c => c.dealId === dealId);
    if (!entry) return;
    entry.contractDate = contractDate;
    localStorage.setItem(`${userId}-${this.#STORAGE_KEY}`, JSON.stringify(list));
  }

  getAll(): RecentContract[] {
    if (!isPlatformBrowser(this.#platformId)) return [];
    const userId = this.#userId();
    if (!userId) return [];
    try {
      return JSON.parse(localStorage.getItem(`${userId}-${this.#STORAGE_KEY}`) ?? '[]');
    } catch {
      return [];
    }
  }

  #userId(): string | null {
    try {
      return JSON.parse(localStorage.getItem('user') ?? '{}')?.id ?? null;
    } catch {
      return null;
    }
  }
}
